import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Stack,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  LinearProgress,
  CircularProgress,
  MenuItem,
  Avatar,
  Tooltip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Palette as PaletteIcon,
  Draw as DrawIcon,
  AutoAwesome as AIIcon,
  Save as SaveIcon,
  SmartToy as BotIcon,
  CloudUpload as CloudUploadIcon,
  Code as CodeIcon,
  Texture as TextureIcon,
  Collections as CollectionsIcon,
  Bookmark as BookmarkIcon
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import MoodboardCanvas from './MoodboardCanvas';
import { useKamSession } from '../../hooks/useKamSession';

// Note: Using native file inputs instead of react-dropzone for simplicity
// Kam Hero Header is now global and rendered in DesignStudioPage.js

// Preset color swatches organized by category
const presetColors = {
  'Neutrals': ['#FFFFFF', '#F5F5F5', '#E8E8E8', '#D1D1D1', '#A8A8A8', '#7D7D7D', '#5A5A5A', '#3D3D3D', '#1F1F1F', '#000000'],
  'Earthy': ['#D4A574', '#C19A6B', '#A0826D', '#8B7355', '#6B5D4F', '#9B8B7E', '#BCB8B1', '#E8DCC4', '#F5E6D3', '#D2B48C'],
  'Pastels': ['#FFE5EC', '#FFC9DE', '#FFB3D9', '#D4A5A5', '#FFCCCB', '#E6E6FA', '#D8BFD8', '#DDA0DD', '#F0E68C', '#FFFACD'],
  'Brights': ['#FF6B6B', '#FF8E53', '#FFC75F', '#FFE66D', '#95E1D3', '#6BCB77', '#4D96FF', '#6C5CE7', '#A29BFE', '#FD79A8'],
  'Jewel Tones': ['#8B008B', '#4B0082', '#191970', '#006400', '#8B0000', '#B8860B', '#2F4F4F', '#556B2F', '#8B4513', '#2C3E50'],
  'Ocean': ['#003F5C', '#2C7FB8', '#41B6C4', '#7FCDBB', '#C7E9B4', '#006D77', '#83C5BE', '#EDF6F9', '#FFDDD2', '#E29578'],
  'Sunset': ['#FF6F61', '#FF9068', '#FFB88C', '#FFD4A3', '#FFEBA1', '#F77F00', '#D62828', '#F94144', '#F8961E', '#F9C74F'],
  'Forest': ['#2D6A4F', '#40916C', '#52B788', '#74C69D', '#95D5B2', '#606C38', '#283618', '#DDA15E', '#BC6C25', '#8B4513']
};

const CollectionPlanWorkspace = ({ brandId, cycleLabel, selectedBimester, bimesterLabel, committedLooks = [] }) => {
  // Kam Session tracking
  const userId = '507f1f77bcf86cd799439011'; // TODO: Get from auth context
  const { sessionId, logActivity, updateContext, saveMoodBoardAnalysis, saveSketchAnalysis } = useKamSession(userId, brandId);

  const [viewMode, setViewMode] = useState('foyer'); // 'foyer' or 'canvas'
  const [moodBoardImages, setMoodBoardImages] = useState([]);
  const [sketches, setSketches] = useState([]);
  const [colorPalette, setColorPalette] = useState([]);
  const [garmentSpecs, setGarmentSpecs] = useState([]);
  const [selectedSketch, setSelectedSketch] = useState(null);
  const [kamAnalyzing, setKamAnalyzing] = useState(false);
  const [yamlDialog, setYamlDialog] = useState(false);
  const [currentYaml, setCurrentYaml] = useState('');
  const [kamFeedback, setKamFeedback] = useState([]);
  const [collectionName, setCollectionName] = useState('');
  const [collectionTheme, setCollectionTheme] = useState('');
  const [uploadType, setUploadType] = useState(null);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#667eea');
  const [bookmarkletDialogOpen, setBookmarkletDialogOpen] = useState(false);

  // Load saved foyer images on mount or when bimester changes
  useEffect(() => {
    const loadFoyerImages = async () => {
      if (!brandId || !selectedBimester) return;

      try {
        const response = await axios.get(`/api/moodboard/load/${brandId}/${selectedBimester}`);
        if (response.data?.success && response.data?.foyerImages?.length > 0) {
          setMoodBoardImages(response.data.foyerImages);
        }
      } catch (error) {
        console.error('Error loading foyer images:', error);
      }
    };

    loadFoyerImages();
  }, [brandId, selectedBimester]);

  // Check for bookmarklet images from URL parameter or localStorage
  useEffect(() => {
    const checkBookmarkletImage = () => {
      // First check URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const bookmarkletData = urlParams.get('bookmarklet');

      if (bookmarkletData) {
        try {
          const imageData = JSON.parse(decodeURIComponent(bookmarkletData));
          const newImage = {
            id: Date.now() + Math.random(),
            url: imageData.url,
            name: imageData.name,
            type: 'moodboard',
            source: 'bookmarklet',
            sourceUrl: imageData.sourceUrl,
            width: imageData.width,
            height: imageData.height
          };
          setMoodBoardImages(prev => [...prev, newImage]);
          toast.success(`Added image from ${new URL(imageData.sourceUrl).hostname}`);

          // Clean up URL
          window.history.replaceState({}, '', window.location.pathname);
        } catch (error) {
          console.error('Error loading bookmarklet image from URL:', error);
        }
      }

      // Also check localStorage as fallback
      const savedImage = localStorage.getItem('sivad_bookmarklet_image');
      if (savedImage) {
        try {
          const imageData = JSON.parse(savedImage);
          const newImage = {
            id: Date.now() + Math.random(),
            url: imageData.url,
            name: imageData.name,
            type: 'moodboard',
            source: 'bookmarklet',
            sourceUrl: imageData.sourceUrl,
            width: imageData.width,
            height: imageData.height
          };
          setMoodBoardImages(prev => [...prev, newImage]);
          toast.success(`Added image from ${new URL(imageData.sourceUrl).hostname}`);
          localStorage.removeItem('sivad_bookmarklet_image');
        } catch (error) {
          console.error('Error loading bookmarklet image from localStorage:', error);
        }
      }
    };

    // Check on mount
    checkBookmarkletImage();

    // Check when window gains focus (user switches back to this tab)
    window.addEventListener('focus', checkBookmarkletImage);
    return () => window.removeEventListener('focus', checkBookmarkletImage);
  }, []);

  // Handle file uploads
  const handleFileUpload = async (event, type) => {
    const files = Array.from(event.target.files);

    if (type === 'moodboard') {
      // Upload each file to server for permanent storage
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('image', file);

        try {
          const response = await axios.post('/api/moodboard/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          if (response.data?.success) {
            return {
              id: Date.now() + Math.random(),
              file,
              url: response.data.imageUrl, // Permanent server URL
              name: file.name,
              type: 'moodboard'
            };
          }
        } catch (error) {
          console.error('Error uploading image:', error);
          // Fallback to blob URL if upload fails
          return {
            id: Date.now() + Math.random(),
            file,
            url: URL.createObjectURL(file),
            name: file.name,
            type: 'moodboard'
          };
        }
      });

      const newImages = await Promise.all(uploadPromises);
      setMoodBoardImages(prev => [...prev, ...newImages.filter(Boolean)]);
      toast.success(`Added ${files.length} image(s) to mood board`);

      // Log activity
      logActivity(
        'collection-plan',
        `Uploaded ${files.length} mood board image(s)`,
        { fileNames: files.map(f => f.name) },
        'User building visual inspiration'
      );
    } else if (type === 'sketch') {
      // Upload each sketch file to server for permanent storage
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('image', file);

        try {
          const response = await axios.post('/api/moodboard/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          if (response.data?.success) {
            return {
              id: Date.now() + Math.random(),
              file,
              url: response.data.imageUrl, // Permanent server URL
              name: file.name,
              analyzed: false,
              kamNotes: null
            };
          }
        } catch (error) {
          console.error('Error uploading sketch:', error);
          // Fallback to blob URL if upload fails
          return {
            id: Date.now() + Math.random(),
            file,
            url: URL.createObjectURL(file),
            name: file.name,
            analyzed: false,
            kamNotes: null
          };
        }
      });

      const newSketches = await Promise.all(uploadPromises);
      setSketches(prev => [...prev, ...newSketches.filter(Boolean)]);
      toast.success(`Added ${files.length} sketch(es)`);

      // Log activity
      logActivity(
        'collection-plan',
        `Uploaded ${files.length} sketch(es)`,
        { fileNames: files.map(f => f.name) },
        'User adding design sketches'
      );
    }
  };

  // Ask Kam to analyze mood board
  const handleAnalyzeMoodBoard = async () => {
    if (moodBoardImages.length === 0) {
      toast.error('Please add images to the mood board first');
      return;
    }

    setKamAnalyzing(true);
    try {
      // Build list of image URLs for Kam to analyze
      const imageList = moodBoardImages.map((img, idx) =>
        `${idx + 1}. ${img.name} - ${img.url}`
      ).join('\n');

      // Build color palette info if available
      const paletteInfo = colorPalette.length > 0
        ? `\n\nColor palette: ${colorPalette.join(', ')}`
        : '';

      const response = await axios.post('/api/assistant/design', {
        messages: [{
          role: 'user',
          content: `I've created a mood board with ${moodBoardImages.length} images for my collection. Please analyze the visual themes, color palette, and design direction.

Collection name: ${collectionName || 'Unnamed'}
Theme: ${collectionTheme || 'Not specified'}
Bimester: ${selectedBimester || 'N/A'} (${bimesterLabel || 'N/A'})

Images in mood board:
${imageList}${paletteInfo}

Based on these images and context, please provide:
1. Key visual themes you observe
2. Color palette analysis
3. Design direction recommendations
4. Suggestions for look zone organization`
        }],
        context: {
          page: 'collection-plan',
          brandId: brandId || '',
          bimester: selectedBimester || '',
          cycleLabel: cycleLabel || bimesterLabel || '',
          collectionName: collectionName || '',
          collectionTheme: collectionTheme || '',
          imageUrls: moodBoardImages.map(img => img.url),
          colorPalette: colorPalette
        }
      });

      const feedback = {
        id: Date.now(),
        type: 'moodboard',
        message: response.data.message,
        timestamp: new Date().toISOString()
      };

      setKamFeedback(prev => [...prev, feedback]);
      toast.success('Kam analyzed your mood board!');
    } catch (error) {
      console.error('Kam analysis error:', error);
      toast.error('Failed to analyze mood board');
    } finally {
      setKamAnalyzing(false);
    }
  };

  // Ask Kam to analyze a sketch
  const handleAnalyzeSketch = async (sketch) => {
    setKamAnalyzing(true);
    setSelectedSketch(sketch);

    try {
      const response = await axios.post('/api/assistant/design', {
        messages: [{
          role: 'user',
          content: `I've uploaded a design sketch (${sketch.name}). Please analyze this sketch and provide:
1. Suggested garment type and silhouette
2. Recommended fabrics based on the design
3. Key construction details you observe
4. Pattern block recommendations
5. Any cultural or design insights

Collection context: ${collectionName || 'Unnamed'}, Theme: ${collectionTheme || 'Not specified'}`
        }],
        context: {
          page: 'collection-plan',
          brandId: brandId || '',
          bimester: selectedBimester || '',
          lookName: collectionName,
          cycleLabel: cycleLabel || bimesterLabel || '',
          collectionName: collectionName || '',
          collectionTheme: collectionTheme || '',
          imageUrls: [sketch.url], // Pass the sketch image to Kam
          sketchName: sketch.name
        }
      });

      // Update sketch with Kam's notes
      setSketches(prev => prev.map(s =>
        s.id === sketch.id
          ? { ...s, analyzed: true, kamNotes: response.data.message }
          : s
      ));

      const feedback = {
        id: Date.now(),
        type: 'sketch',
        sketchId: sketch.id,
        message: response.data.message,
        timestamp: new Date().toISOString()
      };

      setKamFeedback(prev => [...prev, feedback]);
      toast.success('Kam analyzed your sketch!');
    } catch (error) {
      console.error('Kam analysis error:', error);
      toast.error('Failed to analyze sketch');
    } finally {
      setKamAnalyzing(false);
    }
  };

  // Save feedback to journal
  const handleSaveToJournal = async (feedback) => {
    try {
      // Validate brandId
      if (!brandId) {
        console.error('Cannot save to journal: brandId is missing');
        toast.error('Cannot save: Brand ID is missing. Please select a brand.');
        return;
      }

      console.log('Saving to journal:', { brandId, feedbackType: feedback.type });

      const category = feedback.type === 'moodboard' ? 'analyses' :
                      feedback.type === 'sketch' ? 'analyses' : 'notes';

      const response = await axios.post('/api/journal/save', {
        brandId,
        type: feedback.type,
        category,
        title: feedback.type === 'moodboard' ? 'Mood Board Analysis' :
               feedback.type === 'sketch' ? 'Sketch Analysis' : 'Kam Feedback',
        response: feedback.message,
        context: {
          bimester: selectedBimester,
          bimesterLabel,
          collectionName,
          collectionTheme,
          page: 'collection-plan',
          source: 'foyer'
        },
        autoSaved: false,
        generateNote: true,
        tokenLimit: 100
      });

      if (response.data?.success) {
        toast.success('Saved to journal!');
      }
    } catch (error) {
      console.error('Error saving to journal:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      const errorMessage = error.response?.data?.message || 'Failed to save to journal';
      toast.error(errorMessage);
    }
  };

  // Generate YAML for a sketch
  const handleGenerateYAML = async (sketch) => {
    setKamAnalyzing(true);
    setSelectedSketch(sketch);

    try {
      const response = await axios.post('/api/assistant/design', {
        messages: [{
          role: 'user',
          content: `Based on the sketch analysis for ${sketch.name}, please use the generate_yaml_garment function to create a complete YAML garment specification. Include build_id, category, fabric, details, and sizing information.

Previous analysis: ${sketch.kamNotes || 'No analysis available'}

Collection: ${collectionName}, Theme: ${collectionTheme}`
        }],
        context: {
          lookName: collectionName,
          cycleLabel: cycleLabel
        }
      });

      setCurrentYaml(response.data.message);
      setYamlDialog(true);
      toast.success('YAML generated by Kam!');
    } catch (error) {
      console.error('YAML generation error:', error);
      toast.error('Failed to generate YAML');
    } finally {
      setKamAnalyzing(false);
    }
  };

  const handleSaveYAML = () => {
    const newSpec = {
      id: Date.now(),
      sketchId: selectedSketch?.id,
      yaml: currentYaml,
      name: selectedSketch?.name || 'Garment Spec',
      createdAt: new Date().toISOString()
    };
    setGarmentSpecs(prev => [...prev, newSpec]);
    setYamlDialog(false);
    toast.success('Garment specification saved!');
  };

  const handleDeleteMoodBoardImage = (id) => {
    setMoodBoardImages(prev => prev.filter(img => img.id !== id));
    toast.success('Image removed');
  };

  const handleDeleteSketch = (id) => {
    setSketches(prev => prev.filter(s => s.id !== id));
    toast.success('Sketch removed');
  };

  const handleAddColor = () => {
    setColorPickerOpen(true);
  };

  const handleColorPickerClose = () => {
    setColorPickerOpen(false);
    setSelectedColor('#667eea'); // Reset to default
  };

  const handleColorConfirm = () => {
    if (selectedColor && /^#[0-9A-F]{6}$/i.test(selectedColor)) {
      setColorPalette(prev => [...prev, selectedColor]);
      toast.success('Color added to palette');
      setColorPickerOpen(false);
      setSelectedColor('#667eea'); // Reset to default
    } else {
      toast.error('Invalid color format. Use hex format like #FF5733');
    }
  };

  const handleDeleteColor = (idx) => {
    setColorPalette(prev => prev.filter((_, i) => i !== idx));
    toast.success('Color removed from palette');
  };

  // Conditional rendering based on view mode
  if (viewMode === 'canvas') {
    return (
      <Box sx={{ height: 'calc(100vh - 250px)' }}>
        <MoodboardCanvas
          images={moodBoardImages}
          onBack={() => setViewMode('foyer')}
          brandId={brandId}
          selectedBimester={selectedBimester}
          bimesterLabel={bimesterLabel}
          committedLooks={committedLooks}
          kamSession={{
            sessionId,
            cycleLabel,
            collectionName,
            collectionTheme,
          }}
        />
      </Box>
    );
  }

  return (
    <Box>
      {/* View Toggle */}
      <Box sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {moodBoardImages.length === 0 ?
            'Upload images below, then enter Canvas Studio to compose your mood board' :
            `${moodBoardImages.length} images ready for Canvas Studio`
          }
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => setViewMode('canvas')}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            fontWeight: 600,
            px: 4,
            py: 1.5,
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a4198 100%)',
            }
          }}
        >
          🎨 Enter Canvas Studio
        </Button>
      </Box>

      {/* Main Workspace - Foyer */}
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Left Column - Creative Inputs */}
          <Grid item xs={12} md={8}>
            {/* Step 1: Mood Board */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PaletteIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>Step 1: Mood Board</Typography>
                  <Chip label={`${moodBoardImages.length} images`} size="small" color="primary" variant="outlined" />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Alert severity="info" icon={<BotIcon />}>
                    Upload inspiration images, textures, and color references. Kam will analyze the visual direction and help identify key themes.
                  </Alert>

                  {/* Upload Button */}
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="mood-board-upload"
                    multiple
                    type="file"
                    onChange={(e) => handleFileUpload(e, 'moodboard')}
                  />
                  <Stack direction="row" spacing={2}>
                    <label htmlFor="mood-board-upload" style={{ flex: 1 }}>
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        fullWidth
                        sx={{ py: 2 }}
                      >
                        Upload Images
                      </Button>
                    </label>
                    <Button
                      variant="outlined"
                      startIcon={<BookmarkIcon />}
                      onClick={() => setBookmarkletDialogOpen(true)}
                      sx={{ py: 2, whiteSpace: 'nowrap' }}
                    >
                      Save from Web
                    </Button>
                  </Stack>

                  {/* Color Palette */}
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>Color Palette</Typography>
                      <IconButton size="small" onClick={handleAddColor} color="primary">
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {colorPalette.map((color, idx) => (
                        <Box key={idx} sx={{ position: 'relative' }}>
                          <Tooltip title={color}>
                            <Box
                              sx={{
                                width: 56,
                                height: 56,
                                bgcolor: color,
                                borderRadius: 1,
                                border: '3px solid',
                                borderColor: 'divider',
                                cursor: 'pointer',
                                boxShadow: 2,
                                transition: 'transform 0.2s',
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                  boxShadow: 4
                                }
                              }}
                            />
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteColor(idx)}
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              bgcolor: 'error.main',
                              color: 'white',
                              width: 20,
                              height: 20,
                              '&:hover': {
                                bgcolor: 'error.dark'
                              }
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      ))}
                      {colorPalette.length === 0 && (
                        <Typography variant="caption" color="text.secondary">
                          No colors added yet. Click + to add colors.
                        </Typography>
                      )}
                    </Stack>
                  </Box>

                  {/* Mood Board Grid */}
                  {moodBoardImages.length > 0 && (
                    <Grid container spacing={2}>
                      {moodBoardImages.map((img) => (
                        <Grid item xs={6} sm={4} md={3} key={img.id}>
                          <Card sx={{ position: 'relative' }}>
                            <CardMedia
                              component="img"
                              height="160"
                              image={img.url}
                              alt={img.name}
                              sx={{ objectFit: 'cover' }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteMoodBoardImage(img.id)}
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                bgcolor: 'rgba(0,0,0,0.6)',
                                color: 'white',
                                '&:hover': { bgcolor: 'rgba(255,0,0,0.8)' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  {/* Analyze Button */}
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={kamAnalyzing ? <CircularProgress size={20} /> : <AIIcon />}
                    onClick={handleAnalyzeMoodBoard}
                    disabled={kamAnalyzing || moodBoardImages.length === 0}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      py: 1.5
                    }}
                  >
                    {kamAnalyzing ? 'Kam is analyzing...' : 'Ask Kam to Analyze Mood Board'}
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Step 2: Design Sketches */}
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <DrawIcon color="secondary" />
                  <Typography variant="h6" fontWeight={600}>Step 2: Design Sketches</Typography>
                  <Chip label={`${sketches.length} sketches`} size="small" color="secondary" variant="outlined" />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Alert severity="info" icon={<BotIcon />}>
                    Upload your design sketches. Kam will analyze each one and suggest technical specifications, fabrics, and construction details.
                  </Alert>

                  {/* Upload Button */}
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="sketch-upload"
                    multiple
                    type="file"
                    onChange={(e) => handleFileUpload(e, 'sketch')}
                  />
                  <label htmlFor="sketch-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<DrawIcon />}
                      fullWidth
                      color="secondary"
                      sx={{ py: 2 }}
                    >
                      Upload Design Sketches
                    </Button>
                  </label>

                  {/* Sketches Grid */}
                  {sketches.length > 0 ? (
                    <Grid container spacing={2}>
                      {sketches.map((sketch) => (
                        <Grid item xs={12} sm={6} key={sketch.id}>
                          <Card elevation={3}>
                            <CardMedia
                              component="img"
                              height="220"
                              image={sketch.url}
                              alt={sketch.name}
                              sx={{ objectFit: 'cover' }}
                            />
                            <CardContent>
                              <Typography variant="subtitle2" fontWeight={600} gutterBottom noWrap>
                                {sketch.name}
                              </Typography>
                              {sketch.analyzed && (
                                <Chip
                                  label="✓ Analyzed by Kam"
                                  size="small"
                                  color="success"
                                  sx={{ mb: 1 }}
                                />
                              )}
                              {sketch.kamNotes && (
                                <Paper sx={{ p: 1.5, bgcolor: 'grey.50', mb: 1 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {sketch.kamNotes.substring(0, 120)}...
                                  </Typography>
                                </Paper>
                              )}
                              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<AIIcon />}
                                  onClick={() => handleAnalyzeSketch(sketch)}
                                  disabled={kamAnalyzing}
                                >
                                  Analyze
                                </Button>
                                {sketch.analyzed && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<CodeIcon />}
                                    onClick={() => {
                                      setSelectedSketch(sketch);
                                      handleGenerateYAML(sketch);
                                    }}
                                    disabled={kamAnalyzing}
                                    color="success"
                                  >
                                    Generate YAML
                                  </Button>
                                )}
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteSketch(sketch.id)}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                      <DrawIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        No sketches uploaded yet. Upload your design sketches to get started.
                      </Typography>
                    </Paper>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Step 3: Garment Specifications */}
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CodeIcon color="success" />
                  <Typography variant="h6" fontWeight={600}>Step 3: Garment Specifications (YAML)</Typography>
                  <Chip label={`${garmentSpecs.length} specs`} size="small" color="success" variant="outlined" />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {garmentSpecs.length === 0 ? (
                    <Alert severity="info">
                      No garment specifications yet. Analyze your sketches and generate YAMLs to create production-ready specs.
                    </Alert>
                  ) : (
                    garmentSpecs.map((spec) => (
                      <Card key={spec.id} variant="outlined">
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600}>{spec.name}</Typography>
                            <Chip label="YAML" size="small" color="success" />
                          </Stack>
                          <Paper sx={{ p: 2, bgcolor: 'grey.900', maxHeight: 240, overflow: 'auto' }}>
                            <Typography
                              variant="body2"
                              component="pre"
                              sx={{
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap',
                                color: '#a7f3d0',
                                fontSize: '0.85rem'
                              }}
                            >
                              {spec.yaml}
                            </Typography>
                          </Paper>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Created: {new Date(spec.createdAt).toLocaleString()}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Right Column - Kam's Insights */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={4}
              sx={{
                p: 2.5,
                borderRadius: 2,
                position: 'sticky',
                top: 20,
                maxHeight: 'calc(100vh - 100px)',
                display: 'flex',
                flexDirection: 'column',
                background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <BotIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700}>Kam's Insights</Typography>
                  <Typography variant="caption" color="text.secondary">
                    AI Design Assistant
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              {kamAnalyzing && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Kam is analyzing...
                  </Typography>
                </Box>
              )}

              <Stack spacing={2} sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pr: 1, minHeight: 0 }}>
                {kamFeedback.length === 0 ? (
                  <Alert severity="info" icon={<BotIcon />}>
                    No insights yet. Upload your mood board or sketches and ask Kam to analyze them!
                  </Alert>
                ) : (
                  kamFeedback.map((feedback) => (
                    <Card key={feedback.id} elevation={2} sx={{ overflow: 'visible' }}>
                      <CardContent sx={{
                        maxHeight: 'none',
                        overflow: 'visible',
                        '&:last-child': { pb: 2 }
                      }}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              label={feedback.type}
                              size="small"
                              color={feedback.type === 'moodboard' ? 'primary' : 'secondary'}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {new Date(feedback.timestamp).toLocaleTimeString()}
                            </Typography>
                          </Stack>
                          <Tooltip title="Save to Journal">
                            <IconButton
                              size="small"
                              onClick={() => handleSaveToJournal(feedback)}
                              sx={{ color: 'primary.main' }}
                            >
                              <BookmarkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Box sx={{
                          textAlign: 'left',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          '& h1, & h2, & h3': {
                            fontSize: '1rem',
                            fontWeight: 600,
                            mt: 1.5,
                            mb: 0.5,
                            color: 'text.primary',
                            textAlign: 'left',
                            wordBreak: 'break-word'
                          },
                          '& p': {
                            fontSize: '0.875rem',
                            lineHeight: 1.6,
                            my: 0.5,
                            textAlign: 'left',
                            wordBreak: 'break-word'
                          },
                          '& ul, & ol': {
                            pl: 2,
                            my: 0.5,
                            textAlign: 'left',
                            '& li': {
                              fontSize: '0.875rem',
                              lineHeight: 1.5,
                              mb: 0.25,
                              wordBreak: 'break-word'
                            }
                          },
                          '& strong': {
                            fontWeight: 600,
                            color: 'text.primary'
                          }
                        }}>
                          <ReactMarkdown>{feedback.message}</ReactMarkdown>
                        </Box>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* YAML Dialog */}
      <Dialog open={yamlDialog} onClose={() => setYamlDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CodeIcon color="success" />
            <Typography variant="h6">Generated YAML Specification</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={18}
            value={currentYaml}
            onChange={(e) => setCurrentYaml(e.target.value)}
            variant="outlined"
            sx={{
              mt: 1,
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                bgcolor: 'grey.900',
                color: '#a7f3d0'
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setYamlDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveYAML} variant="contained" startIcon={<SaveIcon />} color="success">
            Save Specification
          </Button>
        </DialogActions>
      </Dialog>

      {/* Color Picker Dialog */}
      <Dialog open={colorPickerOpen} onClose={handleColorPickerClose} maxWidth="md" fullWidth>
        <DialogTitle>Add Color to Palette</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 2 }}>
            {/* Preset Color Swatches */}
            <Box>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Choose from Preset Colors
              </Typography>
              <Stack spacing={2}>
                {Object.entries(presetColors).map(([category, colors]) => (
                  <Box key={category}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      {category}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                      {colors.map((color) => (
                        <Tooltip key={color} title={color}>
                          <Box
                            onClick={() => setSelectedColor(color)}
                            sx={{
                              width: 44,
                              height: 44,
                              bgcolor: color,
                              borderRadius: 1,
                              border: selectedColor === color ? '3px solid #3b82f6' : '2px solid',
                              borderColor: selectedColor === color ? '#3b82f6' : 'divider',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              '&:hover': {
                                transform: 'scale(1.15)',
                                boxShadow: 3,
                                zIndex: 1
                              }
                            }}
                          />
                        </Tooltip>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>

            <Divider>OR</Divider>

            {/* Custom Color Picker */}
            <Box>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Custom Color
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <input
                  type="color"
                  value={selectedColor}
                  onChange={(e) => setSelectedColor(e.target.value.toUpperCase())}
                  style={{
                    width: 60,
                    height: 60,
                    border: '3px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
                <TextField
                  label="Hex Code"
                  value={selectedColor}
                  onChange={(e) => {
                    let value = e.target.value.toUpperCase();
                    if (!value.startsWith('#')) {
                      value = '#' + value;
                    }
                    setSelectedColor(value);
                  }}
                  placeholder="#667EEA"
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{
                    maxLength: 7,
                    style: { fontFamily: 'monospace', fontSize: '1rem' }
                  }}
                />
              </Stack>
            </Box>

            {/* Selected Color Preview */}
            <Box>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Selected Color
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  height: 80,
                  bgcolor: selectedColor,
                  borderRadius: 2,
                  border: '3px solid',
                  borderColor: 'divider',
                  boxShadow: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{
                    color: selectedColor,
                    filter: 'invert(1)',
                    mixBlendMode: 'difference'
                  }}
                >
                  {selectedColor}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleColorPickerClose}>Cancel</Button>
          <Button onClick={handleColorConfirm} variant="contained" color="primary">
            Add Color
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bookmarklet Instructions Dialog */}
      <Dialog
        open={bookmarkletDialogOpen}
        onClose={() => setBookmarkletDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <BookmarkIcon color="primary" />
            <Typography variant="h6">Save Images from Any Website</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 2 }}>
            <Alert severity="info">
              Install the bookmarklet to save images directly from Pinterest, Instagram, fashion blogs, and more!
            </Alert>

            {/* Quick Install Section */}
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                📌 Drag this button to your bookmarks bar:
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                <a
                  href={`javascript:(function(){const APP_URL='${window.location.origin}';const images=Array.from(document.querySelectorAll('img')).filter(img=>img.naturalWidth>=200&&img.naturalHeight>=200);if(images.length===0){alert('No suitable images found.');return;}const overlay=document.createElement('div');overlay.style.cssText='position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.9);z-index:999999;display:flex;flex-direction:column;align-items:center;justify-content:center;font-family:system-ui';const header=document.createElement('div');header.style.cssText='color:white;font-size:24px;font-weight:600;margin-bottom:20px';header.textContent='📌 Select an image to save';const closeBtn=document.createElement('button');closeBtn.textContent='✕ Cancel';closeBtn.style.cssText='position:absolute;top:20px;right:20px;padding:10px 20px;background:#f44;color:white;border:none;border-radius:6px;cursor:pointer;font-size:16px;font-weight:600';closeBtn.onclick=()=>document.body.removeChild(overlay);const grid=document.createElement('div');grid.style.cssText='display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:15px;max-width:90%;max-height:70vh;overflow-y:auto;padding:20px;background:rgba(255,255,255,0.05);border-radius:12px';images.forEach(img=>{const c=document.createElement('div');c.style.cssText='position:relative;border-radius:8px;overflow:hidden;cursor:pointer;transition:transform 0.2s;background:#222';const i=document.createElement('img');i.src=img.src;i.style.cssText='width:100%;height:200px;object-fit:cover';const info=document.createElement('div');info.style.cssText='position:absolute;bottom:0;left:0;right:0;background:linear-gradient(to top,rgba(0,0,0,0.9),transparent);color:white;padding:10px;font-size:12px';info.textContent=img.naturalWidth+'×'+img.naturalHeight;c.onmouseover=()=>c.style.transform='scale(1.05)';c.onmouseout=()=>c.style.transform='scale(1)';c.onclick=()=>{const data={url:img.src,name:img.alt||'Image from '+window.location.hostname,width:img.naturalWidth,height:img.naturalHeight,sourceUrl:window.location.href,timestamp:Date.now()};const urlData=encodeURIComponent(JSON.stringify(data));const targetUrl=APP_URL+'?bookmarklet='+urlData;const existingWindow=window.open('','StudioSivadCanvas');if(existingWindow&&!existingWindow.closed){existingWindow.location.href=targetUrl;existingWindow.focus();}else{window.open(targetUrl,'StudioSivadCanvas');}document.body.removeChild(overlay);};c.appendChild(i);c.appendChild(info);grid.appendChild(c);});overlay.appendChild(closeBtn);overlay.appendChild(header);overlay.appendChild(grid);document.body.appendChild(overlay);})();`}
                  onClick={(e) => {
                    e.preventDefault();
                    alert('Please DRAG this button to your bookmarks bar instead of clicking it!');
                  }}
                  style={{
                    display: 'inline-block',
                    padding: '16px 32px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    textDecoration: 'none',
                    borderRadius: '12px',
                    fontWeight: 600,
                    fontSize: '18px',
                    cursor: 'move',
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                    transition: 'transform 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  📌 Save to Canvas
                </a>
              </Box>
              <Alert severity="warning">
                <strong>Don't click the button!</strong> Drag and drop it onto your browser's bookmarks bar.
              </Alert>
            </Box>

            {/* Instructions */}
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                How to use:
              </Typography>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={2}>
                  <Chip label="1" color="primary" />
                  <Typography variant="body2">
                    Show your bookmarks bar (Cmd/Ctrl + Shift + B)
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2}>
                  <Chip label="2" color="primary" />
                  <Typography variant="body2">
                    Drag the "Save to Canvas" button above to your bookmarks bar
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2}>
                  <Chip label="3" color="primary" />
                  <Typography variant="body2">
                    Visit any website (Pinterest, Instagram, fashion blogs, etc.)
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2}>
                  <Chip label="4" color="primary" />
                  <Typography variant="body2">
                    Click the "Save to Canvas" bookmark to select and save images
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Alert severity="success">
              Works on Pinterest, Instagram, Unsplash, fashion blogs, e-commerce sites, and more!
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookmarkletDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CollectionPlanWorkspace;
